import React, { useState, useEffect } from 'react';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { TrendingUp, ShieldCheck, AlertTriangle, Award } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis as RawPolarAngleAxis, PolarRadiusAxis as RawPolarRadiusAxis
} from 'recharts';
import { clientDashboardService } from '../../services/clientDashboard';

// Workaround: recharts 3.x retorna ReactNode que inclui undefined, incompatível com JSX do React 19
const PolarAngleAxis = RawPolarAngleAxis as any;
const PolarRadiusAxis = RawPolarRadiusAxis as any;

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
  refreshKey: number;
}

const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#06b6d4'];

function formatSeconds(s: number): string {
  if (!s || s <= 0) return '-';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec.toString().padStart(2, '0')}s`;
}

const ClientDashboardConsolidated: React.FC<Props> = ({ filters, refreshKey }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await clientDashboardService.getConsolidatedAdvanced(filters);
        setData(res.data?.transportadoras || []);
      } catch (err: any) {
        if (err.response?.status !== 401) {
          setError('Erro ao carregar dashboard consolidado. Tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.periodo, filters.dataInicio, filters.dataFim, refreshKey]);

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="client-dashboard-spinner" />
        Carregando dashboard consolidado...
      </div>
    );
  }

  if (error) {
    return <div className="client-dashboard-error-message">{error}</div>;
  }

  if (data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <h2 className="client-dashboard-section-title">Dashboard Consolidado</h2>
        <p style={{ color: '#94a3b8', fontSize: 15 }}>
          Nenhuma transportadora disponível para consolidação. Verifique se seu acesso está ativo em pelo menos uma transportadora.
        </p>
      </div>
    );
  }

  // Ranking: melhor taxa de perfeição
  const ranking = [...data].sort((a, b) => b.taxa_perfeicao - a.taxa_perfeicao);
  const maxEtiquetas = Math.max(...data.map(t => t.total_etiquetas || 0), 1);

  // Dados para gráfico radar comparativo
  const radarData = buildRadarData(data);

  // Dados para gráfico de evolução comparativa
  const evolucaoComparativa = buildEvolucaoComparativa(data);

  // Dados do gráfico de barras comparativo
  const chartData = data.map(t => ({
    name: t.empresa_nome,
    etiquetas: t.total_etiquetas,
    conferencias: t.total_conferencias,
    paletes: t.total_paletes,
    divergencias: t.total_divergencias
  }));

  return (
    <>
      <h2 className="client-dashboard-section-title">Dashboard Consolidado - Gestão Multi Transportadoras</h2>

      {/* Ranking de Transportadoras */}
      <div className="client-dashboard-config-card" style={{ marginBottom: 24 }}>
        <h3 className="client-dashboard-chart-title">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Award size={16} /> Ranking de Transportadoras
          </span>
        </h3>
        <div className="client-analytics-ranking">
          {ranking.map((t, i) => (
            <div key={i} className="client-analytics-ranking-item">
              <div className="client-analytics-ranking-pos" style={{
                background: i === 0 ? 'rgba(234,179,8,0.2)' : i === 1 ? 'rgba(148,163,184,0.2)' : i === 2 ? 'rgba(205,127,50,0.2)' : 'rgba(148,163,184,0.1)',
                color: i === 0 ? '#facc15' : i === 1 ? '#cbd5e1' : i === 2 ? '#f97316' : '#94a3b8'
              }}>
                {i + 1}
              </div>
              <div className="client-analytics-ranking-info">
                <span className="client-analytics-ranking-name">{t.empresa_nome}</span>
                <span className="client-analytics-ranking-details">
                  {t.total_conferencias} conf. | {t.total_etiquetas.toLocaleString('pt-BR')} etiq. | {t.total_paletes} paletes
                </span>
              </div>
              <div className="client-analytics-ranking-metrics">
                <div className="client-analytics-ranking-metric">
                  <ShieldCheck size={14} style={{ color: '#22c55e' }} />
                  <span style={{ color: '#4ade80' }}>{t.taxa_perfeicao}%</span>
                  <span style={{ color: '#64748b', fontSize: 11 }}>perfeição</span>
                </div>
                <div className="client-analytics-ranking-metric">
                  <TrendingUp size={14} style={{ color: '#3b82f6' }} />
                  <span style={{ color: '#60a5fa' }}>{t.taxa_conclusao}%</span>
                  <span style={{ color: '#64748b', fontSize: 11 }}>conclusão</span>
                </div>
                <div className="client-analytics-ranking-metric">
                  <AlertTriangle size={14} style={{ color: '#ef4444' }} />
                  <span style={{ color: '#f87171' }}>{t.conferencias_divergentes}</span>
                  <span style={{ color: '#64748b', fontSize: 11 }}>diverg.</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPIs Comparativos */}
      <div className="client-dashboard-metrics-grid" style={{ marginBottom: 24 }}>
        {data.map((t, i) => (
          <div key={i} className="client-dashboard-config-card" style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: COLORS[i % COLORS.length] }} />
              <span style={{ fontWeight: 600, color: '#e2e8f0' }}>{t.empresa_nome}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9' }}>{t.taxa_perfeicao}%</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Taxa de Perfeição</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9' }}>{t.taxa_conclusao}%</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Taxa de Conclusão</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9' }}>{formatSeconds(t.tempo_medio_conferencia)}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Tempo Médio</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: t.conferencias_divergentes > 0 ? '#f87171' : '#4ade80' }}>
                  {t.conferencias_divergentes}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Divergentes</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gráfico Radar Comparativo */}
      <div className="client-dashboard-charts">
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Radar Comparativo</h3>
          <ResponsiveContainer width="100%" height={350}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="rgba(148,163,184,0.2)" />
              <PolarAngleAxis dataKey="metric" stroke="#94a3b8" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis stroke="#64748b" tick={{ fontSize: 10 }} />
              {data.map((t, i) => (
                <Radar
                  key={i}
                  name={t.empresa_nome}
                  dataKey={t.empresa_nome}
                  stroke={COLORS[i % COLORS.length]}
                  fill={COLORS[i % COLORS.length]}
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
              ))}
              <Legend />
              <Tooltip contentStyle={{
                background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '8px', color: '#e2e8f0'
              }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Comparativo de Volume */}
        <div className="client-dashboard-chart-container">
          <h3 className="client-dashboard-chart-title">Comparativo Geral</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 12 }} />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{
                background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '8px', color: '#e2e8f0'
              }} />
              <Bar dataKey="etiquetas" fill="#3b82f6" name="Etiquetas" radius={[4, 4, 0, 0]} />
              <Bar dataKey="conferencias" fill="#22c55e" name="Conferências" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paletes" fill="#eab308" name="Paletes" radius={[4, 4, 0, 0]} />
              <Bar dataKey="divergencias" fill="#ef4444" name="Divergências" radius={[4, 4, 0, 0]} />
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Evolução Comparativa Diária */}
      {evolucaoComparativa.length > 0 && (
        <div className="client-dashboard-chart-container" style={{ marginBottom: 24 }}>
          <h3 className="client-dashboard-chart-title">Evolução Diária - Conferências por Transportadora</h3>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={evolucaoComparativa}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="dia" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{
                background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '8px', color: '#e2e8f0'
              }} />
              {data.map((t, i) => (
                <Line
                  key={i}
                  type="monotone"
                  dataKey={t.empresa_nome}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Volume de Etiquetas por Transportadora */}
      <div className="client-dashboard-config-card" style={{ marginBottom: 24 }}>
        <h3 className="client-dashboard-chart-title">Volume de Etiquetas por Transportadora</h3>
        {data.map((t, i) => (
          <div key={i} className="client-dashboard-comparison-bar">
            <span className="client-dashboard-comparison-name">{t.empresa_nome}</span>
            <div className="client-dashboard-comparison-track">
              <div
                className="client-dashboard-comparison-fill"
                style={{
                  width: `${(t.total_etiquetas / maxEtiquetas) * 100}%`,
                  background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[(i + 1) % COLORS.length]})`
                }}
              >
                {t.total_etiquetas.toLocaleString('pt-BR')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tipos de Conferência por Transportadora */}
      {data.some(t => (t.tipos_conferencia || []).length > 0) && (
        <div className="client-dashboard-config-card" style={{ marginBottom: 24 }}>
          <h3 className="client-dashboard-chart-title">Tipos de Conferência por Transportadora</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="client-dashboard-table">
              <thead>
                <tr>
                  <th>Transportadora</th>
                  <th>Pré-Carregamento</th>
                  <th>Carregamento</th>
                  <th>Descarregamento</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t, i) => {
                  const tipos = t.tipos_conferencia || [];
                  const pre = tipos.find((x: any) => x.tipo === 'pre_carregamento');
                  const car = tipos.find((x: any) => x.tipo === 'carregamento');
                  const des = tipos.find((x: any) => x.tipo === 'descarregamento');
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                          {t.empresa_nome}
                        </div>
                      </td>
                      <td>{pre ? `${pre.total} conf. / ${pre.itens || 0} itens` : '-'}</td>
                      <td>{car ? `${car.total} conf. / ${car.itens || 0} itens` : '-'}</td>
                      <td>{des ? `${des.total} conf. / ${des.itens || 0} itens` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Áreas de Entrega e Top Ocorrências por Transportadora */}
      <div className="client-dashboard-charts">
        {data.filter(t => (t.top_areas || []).length > 0).map((t, i) => (
          <div key={i} className="client-dashboard-chart-container">
            <h3 className="client-dashboard-chart-title">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                Top Áreas de Entrega - {t.empresa_nome}
              </span>
            </h3>
            {t.top_areas.map((r: any, ri: number) => {
              const label = r.area_codigo && r.area_nome ? `${r.area_codigo} (${r.area_nome})` : (r.area_codigo || r.area_nome || '-');
              return (
                <div key={ri} className="client-dashboard-comparison-bar">
                  <span className="client-dashboard-comparison-name" style={{ minWidth: 140, fontSize: 13 }}>
                    {label.length > 22 ? label.substring(0, 22) + '...' : label}
                  </span>
                  <div className="client-dashboard-comparison-track">
                    <div
                      className="client-dashboard-comparison-fill"
                      style={{
                        width: `${(r.total / Math.max(...t.top_areas.map((x: any) => x.total), 1)) * 100}%`,
                        background: COLORS[i % COLORS.length]
                      }}
                    >
                      {r.total}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Tabela Detalhada Comparativa */}
      <div className="client-dashboard-table-container">
        <h3 className="client-dashboard-table-title">Performance Detalhada - Comparativo</h3>
        <table className="client-dashboard-table">
          <thead>
            <tr>
              <th>Transportadora</th>
              <th>Etiquetas</th>
              <th>Conferências</th>
              <th>Paletes</th>
              <th>Divergências</th>
              <th>Tempo Médio</th>
              <th>Conclusão</th>
              <th>Perfeição</th>
            </tr>
          </thead>
          <tbody>
            {data.map((t, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                    {t.empresa_nome}
                  </div>
                </td>
                <td>{t.total_etiquetas.toLocaleString('pt-BR')}</td>
                <td>{t.total_conferencias}</td>
                <td>{t.total_paletes}</td>
                <td>
                  <span className={`client-dashboard-badge ${t.total_divergencias > 0 ? 'divergente' : 'concluido'}`}>
                    {t.total_divergencias}
                  </span>
                </td>
                <td>{formatSeconds(t.tempo_medio_conferencia)}</td>
                <td>
                  <span style={{ color: t.taxa_conclusao >= 90 ? '#4ade80' : t.taxa_conclusao >= 70 ? '#facc15' : '#f87171', fontWeight: 600 }}>
                    {t.taxa_conclusao}%
                  </span>
                </td>
                <td>
                  <span style={{ color: t.taxa_perfeicao >= 90 ? '#4ade80' : t.taxa_perfeicao >= 70 ? '#facc15' : '#f87171', fontWeight: 600 }}>
                    {t.taxa_perfeicao}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Constrói dados para gráfico radar normalizado
function buildRadarData(transportadoras: any[]): any[] {
  if (transportadoras.length === 0) return [];

  const metrics = [
    { key: 'total_etiquetas', label: 'Volume' },
    { key: 'taxa_conclusao', label: 'Conclusão' },
    { key: 'taxa_perfeicao', label: 'Perfeição' },
    { key: 'total_paletes', label: 'Paletes' },
    { key: 'total_conferencias', label: 'Conferências' }
  ];

  return metrics.map(m => {
    const row: any = { metric: m.label };
    const maxVal = Math.max(...transportadoras.map(t => t[m.key] || 0), 1);
    transportadoras.forEach(t => {
      // Normalizar para 0-100
      row[t.empresa_nome] = m.key.startsWith('taxa_')
        ? (t[m.key] || 0)
        : Math.round(((t[m.key] || 0) / maxVal) * 100);
    });
    return row;
  });
}

// Constrói dados de evolução comparativa
function buildEvolucaoComparativa(transportadoras: any[]): any[] {
  const allDays: Record<string, any> = {};

  transportadoras.forEach(t => {
    (t.evolucao_diaria || []).forEach((d: any) => {
      const diaFormatted = new Date(d.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      if (!allDays[diaFormatted]) allDays[diaFormatted] = { dia: diaFormatted };
      allDays[diaFormatted][t.empresa_nome] = d.total;
    });
  });

  return Object.values(allDays).sort((a: any, b: any) => {
    const [dA, mA] = a.dia.split('/').map(Number);
    const [dB, mB] = b.dia.split('/').map(Number);
    return mA !== mB ? mA - mB : dA - dB;
  });
}

export default ClientDashboardConsolidated;
